import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { SingleValue } from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import { setDomain } from '@/app/common/reducers/domainSlice';
import { subscribePromptRunBatch, trackerAiClient as api } from '@/app/TrackerAi/api/trackerAiClient';
import { useProject } from '../context/ProjectContext';
import {
  dismissProjectSetup,
  isProjectSetupDismissed,
} from '../utils/setup-onboarding-storage';
import type { CompetitorInsight } from '../types';
import { useCompetitorForm } from '../components/CompetitorForm';
import { useNewPromptForm } from '../components/NewPromptForm';
import type { SetupDomainOptionData, SetupStep, PromptSetupPhase, UserDomain } from '../components/project-setup/types';
import { trackerAiPath } from '../utils/paths';

type SelectedDomainState = {
  name?: string;
  slug?: string;
  show_name?: string;
};

function buildDomainOptions(domains: UserDomain[]): SetupDomainOptionData[] {
  return domains.map((domain) => ({
    label: domain.url,
    value: domain.slug,
    show_name: domain.show_name ?? undefined,
  }));
}

function resolveSelectedDomainOption(
  domainOptions: SetupDomainOptionData[],
  domains: UserDomain[],
  selectedDomain: SelectedDomainState,
): SetupDomainOptionData | null {
  const slug = selectedDomain?.slug ?? domains[0]?.slug;
  const matched = domainOptions.find((option) => option.value === slug);
  if (matched) return matched;
  if (!selectedDomain?.slug || selectedDomain.slug === 'no-domain') return null;
  return {
    label: selectedDomain.name ?? '',
    value: selectedDomain.slug,
    show_name: selectedDomain.show_name ?? undefined,
  };
}

export function useProjectSetupOnboarding() {
  const {
    projectId,
    project,
    projectsLoaded,
    data,
    loading,
    topics,
    models,
    defaultModelId,
    topicFilterId,
    handleCreatePrompt,
    creatingPrompt,
    refresh,
    setError,
    ensureProject,
    ensuringProject,
    setupOpenSignal,
  } = useProject();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const selectedDomain = useSelector(
    (state: { selectedDomain?: SelectedDomainState }) => state.selectedDomain ?? {},
  );
  const { data: userInfo } = useGetUserInfoQuery();

  const domains = (userInfo?.data?.domains ?? []) as UserDomain[];
  const domainOptions = useMemo(() => buildDomainOptions(domains), [domains]);
  const selectedDomainOption = useMemo(
    () => resolveSelectedDomainOption(domainOptions, domains, selectedDomain),
    [domainOptions, domains, selectedDomain],
  );

  const [ready, setReady] = useState(false);
  const [step, setStep] = useState<SetupStep>('info');
  const [promptPhase, setPromptPhase] = useState<PromptSetupPhase>('form');
  const [savingCompetitor, setSavingCompetitor] = useState(false);
  const [deletingCompetitorId, setDeletingCompetitorId] = useState<string | null>(null);
  const [competitors, setCompetitors] = useState<CompetitorInsight[]>([]);
  const [addDomainDrawerOpen, setAddDomainDrawerOpen] = useState(false);
  const [aliases, setAliases] = useState<string[]>([]);
  const promptRunStarted = useRef(false);

  useEffect(
    () =>
      subscribePromptRunBatch(() => {
        promptRunStarted.current = true;
      }),
    [],
  );

  const domainSlug = selectedDomain?.slug?.trim();
  const domainUrl = selectedDomain?.name?.trim();
  const hasValidDomain = Boolean(domainSlug && domainSlug !== 'no-domain' && domainUrl);
  const onboardingPath = trackerAiPath('onboarding', domainSlug, domainUrl);
  const overviewPath = trackerAiPath('overview', domainSlug, domainUrl);
  const onOnboarding = location.pathname.includes('/tracker-ai/onboarding/');

  const promptCount = data?.insight.counts.prompts ?? 0;
  const hasPrompts = promptCount > 0;
  const hasCompetitors = competitors.length > 0;
  const needsProject = hasValidDomain && projectsLoaded && !project;

  const competitorForm = useCompetitorForm({ active: onOnboarding && step === 'competitor' });
  const promptForm = useNewPromptForm({
    topics,
    models,
    defaultModelId,
    defaultTopicId: topicFilterId,
    active: onOnboarding && step === 'prompt' && !hasPrompts && promptPhase === 'form',
  });

  const loadCompetitors = useCallback(async () => {
    if (!projectId) {
      setCompetitors([]);
      return [];
    }
    const list = (await api.getCompetitors(projectId)) as CompetitorInsight[];
    setCompetitors(list);
    return list;
  }, [projectId]);

  useEffect(() => {
    if (!hasValidDomain) {
      setReady(true);
      return;
    }

    if (needsProject) {
      setStep('info');
      setReady(true);
      return;
    }

    if (!projectId || loading || !data) return;

    if (!onOnboarding && isProjectSetupDismissed(projectId) && promptPhase === 'form') {
      setReady(true);
      return;
    }

    let cancelled = false;

    void loadCompetitors()
      .then((list) => {
        if (cancelled) return;
        const hasComp = list.length > 0;
        const hasP = (data.insight.counts.prompts ?? 0) > 0;
        if (!hasComp || !hasP) {
          setStep((prev) => (prev === 'competitor' ? prev : hasComp ? 'prompt' : 'competitor'));
        }
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setCompetitors([]);
        setStep((prev) => (prev === 'competitor' ? prev : 'competitor'));
        setReady(true);
      });

    return () => {
      cancelled = true;
    };
  }, [
    hasValidDomain,
    needsProject,
    projectId,
    loading,
    data,
    loadCompetitors,
    promptPhase,
    onOnboarding,
    setupOpenSignal,
  ]);

  useEffect(() => {
    if (!onOnboarding || step !== 'competitor' || !projectId) return;
    void loadCompetitors().catch(() => setCompetitors([]));
  }, [onOnboarding, step, projectId, loadCompetitors]);

  const dismissed = Boolean(projectId && isProjectSetupDismissed(projectId));
  const incomplete = needsProject || (ready && !(hasCompetitors && hasPrompts));
  const shouldEnter = hasValidDomain && incomplete && !dismissed && promptPhase === 'form';

  useEffect(() => {
    if (!hasValidDomain) return;
    if (!needsProject && !ready) return;
    if (shouldEnter && location.pathname !== onboardingPath) {
      navigate(onboardingPath, { replace: true });
      return;
    }
    if (
      onOnboarding &&
      promptPhase === 'form' &&
      ready &&
      !needsProject &&
      hasCompetitors &&
      hasPrompts
    ) {
      navigate(overviewPath, { replace: true });
    }
  }, [
    hasValidDomain,
    needsProject,
    ready,
    shouldEnter,
    onOnboarding,
    promptPhase,
    hasCompetitors,
    hasPrompts,
    location.pathname,
    onboardingPath,
    overviewPath,
    navigate,
  ]);

  const finishSetup = useCallback(() => {
    if (projectId) dismissProjectSetup(projectId);
    navigate(overviewPath, { replace: true });
  }, [projectId, navigate, overviewPath]);

  const removeAlias = useCallback((index: number) => {
    setAliases((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const addAlias = useCallback((name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setAliases((prev) => {
      const exists = prev.some(
        (alias) => alias.trim().toLowerCase() === trimmed.toLowerCase(),
      );
      if (exists) return prev;
      return [...prev, trimmed];
    });
  }, []);

  const handleEnsureProject = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      if (!domainSlug || !domainUrl) return;
      setError('');
      const trimmedAliases = aliases.map((a) => a.trim()).filter(Boolean);
      try {
        await ensureProject({
          slug: domainSlug,
          domain: domainUrl,
          ...(trimmedAliases.length ? { aliases: trimmedAliases } : {}),
        });
        setStep('competitor');
      } catch {
        // error in context
      }
    },
    [domainSlug, domainUrl, aliases, ensureProject, setError],
  );

  const handleDomainChange = useCallback(
    (option: SingleValue<SetupDomainOptionData>) => {
      if (!option) return;
      dispatch(
        setDomain({
          name: option.label,
          slug: option.value,
          show_name: option.show_name,
        }),
      );
    },
    [dispatch],
  );

  const handleSaveCompetitor = useCallback(async () => {
    const values = competitorForm.getValues();
    if (!values || !projectId) return;
    setSavingCompetitor(true);
    setError('');
    try {
      await api.createCompetitor(projectId, values);
      competitorForm.reset();
      await loadCompetitors();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingCompetitor(false);
    }
  }, [competitorForm, projectId, loadCompetitors, setError]);

  const handleDeleteCompetitor = useCallback(
    async (competitorId: string) => {
      if (!projectId) return;
      setDeletingCompetitorId(competitorId);
      setError('');
      try {
        await api.deleteCompetitor(projectId, competitorId);
        await loadCompetitors();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setDeletingCompetitorId(null);
      }
    },
    [projectId, loadCompetitors, setError],
  );

  const completePromptTracking = useCallback(() => {
    setPromptPhase('done');
  }, []);

  const handleSavePrompt = useCallback(async () => {
    const values = promptForm.getValues();
    if (!values) return;
    promptRunStarted.current = false;
    setPromptPhase('tracking');
    try {
      await handleCreatePrompt({ ...values, awaitRuns: true });
      // shortcut: no queued batch (all skipped or enqueue failed) skips the bar
      if (!promptRunStarted.current) setPromptPhase('done');
    } catch {
      setPromptPhase('form');
    }
  }, [promptForm, handleCreatePrompt]);

  return {
    ready,
    onboardingPath,
    step,
    promptPhase,
    addDomainDrawerOpen,
    setAddDomainDrawerOpen,
    domainOptions,
    selectedDomainOption,
    domainUrl,
    project,
    competitors,
    competitorForm,
    promptForm,
    needsProject,
    hasValidDomain,
    hasCompetitors,
    hasPrompts,
    ensuringProject,
    savingCompetitor,
    deletingCompetitorId,
    creatingPrompt,
    aliases,
    removeAlias,
    addAlias,
    handleEnsureProject,
    handleDomainChange,
    handleSaveCompetitor,
    handleDeleteCompetitor,
    handleSavePrompt,
    completePromptTracking,
    finishSetup,
    setStep,
  };
}

const ProjectSetupContext = createContext<ReturnType<typeof useProjectSetupOnboarding> | null>(null);

export function ProjectSetupProvider({ children }: { children: ReactNode }) {
  const setup = useProjectSetupOnboarding();
  return <ProjectSetupContext.Provider value={setup}>{children}</ProjectSetupContext.Provider>;
}

export function useProjectSetup() {
  const setup = useContext(ProjectSetupContext);
  if (!setup) throw new Error('useProjectSetup must be used within ProjectSetupProvider');
  return setup;
}
